/**
 * Formats and validates phone numbers
 */

/**
 * Formats a phone number to (XXX) XXX-XXXX format
 */
export const formatPhoneNumber = (value: string): string => {
  const phoneNumber = value.replace(/\D/g, '');
  const phoneNumberLength = phoneNumber.length;

  if (phoneNumberLength < 4) return phoneNumber;
  if (phoneNumberLength < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  }
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
};

/**
 * Validates a phone number (US format)
 * Accepts: 10 digits, optionally with country code 1
 */
export const validatePhoneNumber = (phone: string): boolean => {
  const cleaned = phone.replace(/\D/g, '');
  // Must be 10 digits (US) or 11 digits starting with 1
  if (cleaned.length === 10) {
    return /^[2-9]\d{2}[2-9]\d{2}\d{4}$/.test(cleaned);
  }
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return /^1[2-9]\d{2}[2-9]\d{2}\d{4}$/.test(cleaned);
  }
  return false;
};

/**
 * Normalizes a phone number to digits only (10 or 11 digits)
 * Returns empty string if invalid
 */
export const normalizePhoneNumber = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, '');
  // If 11 digits starting with 1, keep it; if 10 digits, keep it; otherwise return empty
  if (cleaned.length === 10) {
    return cleaned;
  }
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return cleaned;
  }
  return '';
};

/**
 * Formats and validates email addresses
 */

/**
 * Normalizes an email address (lowercase, trim)
 */
export const normalizeEmail = (email: string): string => {
  return email.trim().toLowerCase();
};

/**
 * Validates an email address format
 */
export const validateEmail = (email: string): boolean => {
  const normalized = normalizeEmail(email);
  // RFC 5322 compliant regex (simplified but covers most cases)
  const emailRegex = /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i;
  return emailRegex.test(normalized);
};

/**
 * Cleans URLs by removing encoding artifacts and double encoding
 */
export const cleanUrl = (url: string | undefined | null): string => {
  if (!url) return '';
  
  try {
    // Remove %% encoding artifacts
    let cleaned = url.replace(/%%/g, '');
    
    // Try to decode if it looks encoded
    if (cleaned.includes('%')) {
      try {
        // Decode once
        cleaned = decodeURIComponent(cleaned);
        // If it still has %, decode again (handles double encoding)
        if (cleaned.includes('%')) {
          cleaned = decodeURIComponent(cleaned);
        }
      } catch (e) {
        // If decoding fails, just remove % characters that aren't valid encoding
        cleaned = cleaned.replace(/%[^0-9A-Fa-f]{2}/g, '');
      }
    }
    
    // Validate it's a proper URL
    try {
      new URL(cleaned);
      return cleaned;
    } catch {
      // If not a valid URL, try to construct one
      if (cleaned.startsWith('http://') || cleaned.startsWith('https://')) {
        return cleaned;
      }
      // If it looks like a URL but missing protocol, add https
      if (cleaned.includes('.') && !cleaned.includes(' ')) {
        return `https://${cleaned}`;
      }
      return cleaned;
    }
  } catch (error) {
    // If anything fails, return the original string cleaned of %% only
    return url.replace(/%%/g, '');
  }
};
